import { CircularProgress, makeStyles } from "@material-ui/core";
import Authorize from "components/Authorize";
import LoadingSpinner from "components/LoadingSpinner";
import { useGetClaimClientIdQuery, useGetHasAccessToAllClientsQuery, useGetTenantNameQuery, useGetClaimClientAccountIdQuery, useGetIsVendorQuery } from "features/me/meSlice";
import { useTitle } from "hooks/useTitle";
import { jwtDecode } from "jwt-decode";
import qs from "qs";
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Route, useLocation } from "react-router-dom";
import { useSessionstorageState } from "rooks";
import { useAuth } from "react-oidc-context";
import { setToken } from "../user/seamlessTokenSlice";
import { setPermissionsLoaded, selectPermissionsLoaded, selectUserPermissions, selectTenantName, selectUserClientAccountId, selectUserClientId, setClientAccountId, setClientId, setHasAccessToAllClients, setTenantName, hasAccessToAllClients, setIsVendor, selectIsVendor } from "../user/userSlice";
import UnauthorizedRoute from "./UnauthorizedRoute/UnauthorizedRoute";

import { TenantFeatureFlag } from "features/saasManagement/TenantConfig/TenantFeatureFlag";

const useStyles = makeStyles((theme) => ({
    table: {
        marginTop: 20
    },
    spinner: {
        position: 'absolute',
        top: '50%',
        left: "calc(50% - 20px)",
        zIndex: '100'
    }
}));

const PrivateRoute = ({ component: Component, profile, featureFlagName, pageTitle, showSideNav = true, ...rest }) => {
    const classes = useStyles();
    const location = useLocation();
    const dispatch = useDispatch();
    const auth = useAuth();

    const [redirectPath, setRedirectPath] = useSessionstorageState("redirectPath", "/dashboard");

    const tenantName = useSelector(selectTenantName);
    const userClientId = useSelector(selectUserClientId);
    const userClientAccountId = useSelector(selectUserClientAccountId);
    const userHasAccessToAllClients = useSelector(hasAccessToAllClients);
    const userIsVendor = useSelector(selectIsVendor);
    const userPermissions = useSelector(selectUserPermissions);
    const permissionsLoaded = useSelector(selectPermissionsLoaded);

    const [userState, setUserState] = useState(null);

    const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true });
    const token = queryParams?.token;
    const seamlessToken = token;
    let allowSeamless = !!seamlessToken;

    if (seamlessToken) {
        const decoded = jwtDecode(seamlessToken);
        const vehicleId = decoded.vehicleId;
        allowSeamless = allowSeamless && decoded.role === "seamless" && !!vehicleId && location.pathname === `/assets/detail/${vehicleId}`;
    }

    const user = auth.user;
    const isAuthenticated = !!user && !user.expired;


    const triedRef = useRef(false);

    useEffect(() => {
        if (triedRef.current) return;

        if (!isAuthenticated && !allowSeamless && !auth.activeNavigator && !auth.isLoading) {
            triedRef.current = true;

            const search = seamlessToken ? "" : location.search;
            const path = (location.pathname || "/Dashboard") + search;
            setRedirectPath(path || "/Dashboard");

            auth.signinSilent()
                .then((res) => {
                    if (!res) return auth.signinRedirect({ state: { returnTo: path } });
                })
                .catch(() => auth.signinRedirect({ state: { returnTo: path } }));
        }
    }, [
        isAuthenticated,
        allowSeamless,
        auth.activeNavigator,
        auth.isLoading,
        location.pathname,
        location.search,
        seamlessToken,
    ]);

    useEffect(() => {
        triedRef.current = false;
    }, [location.pathname]);

    useEffect(() => {
        if (seamlessToken) {
            dispatch(setToken(seamlessToken));
        }
    }, [seamlessToken]);

    useEffect(() => {
        if (!isAuthenticated || permissionsLoaded || !userPermissions?.length) return;

        dispatch(setPermissionsLoaded(true));
    }, [isAuthenticated, permissionsLoaded, userPermissions?.length]);


    const { data: userTenant } = useGetTenantNameQuery(undefined, {
        skip: !isAuthenticated || userState?.expired || tenantName?.length > 0
    });
    const { data: clientId } = useGetClaimClientIdQuery(undefined, {
        skip: !isAuthenticated || userState?.expired || userClientId?.length > 0,
    });
    const { data: clientAccountId } = useGetClaimClientAccountIdQuery(undefined, {
        skip: !isAuthenticated || userState?.expired || userClientAccountId?.length > 0,
    });
    const { data: hasAccessToAllClientsApi } = useGetHasAccessToAllClientsQuery(undefined, {
        skip: !isAuthenticated || userState?.expired || userHasAccessToAllClients?.length > 0,
    });
    const { data: isVendorApi } = useGetIsVendorQuery(undefined, {
        skip: !isAuthenticated || userState?.expired || userIsVendor === true,
    });

    useEffect(() => {
        if (clientId) dispatch(setClientId(clientId));
        if (userTenant?.data?.name) dispatch(setTenantName(userTenant.data.name));
        if (clientAccountId) dispatch(setClientAccountId(clientAccountId));
        if (hasAccessToAllClientsApi != null) dispatch(setHasAccessToAllClients(hasAccessToAllClientsApi));
        if (isVendorApi != null) dispatch(setIsVendor(isVendorApi?.data ?? isVendorApi));
    }, [clientId, userTenant, clientAccountId, hasAccessToAllClientsApi, isVendorApi]);

    useTitle(pageTitle ?? "");
    const isAccessReady = isAuthenticated || allowSeamless;

    return (
        <Authorize profile={profile} unauthorizedComponent={<>{!userState ? <LoadingSpinner loading={true} /> : <UnauthorizedRoute />}</>}>
            <TenantFeatureFlag featureFlagName={featureFlagName}>
                <Route
                    {...rest}
                    render={(props) =>
                        isAccessReady ? (
                            <Component {...props} />
                        ) : (
                            <CircularProgress className={classes.spinner} />
                        )
                    }
                />
            </TenantFeatureFlag>
        </Authorize>
    );
};

export default PrivateRoute;